import {connect} from 'react-redux';
import {flow, get} from 'lodash/fp';
import {createStructuredSelector} from 'reselect';

import {pageSize} from '/constant/data.constant';
import {
  PAGE_NUMBER_FIELD_NAME,
  PAGE_SIZE_FIELD_NAME,
} from '/enum/apiFields.enum';
import {COACH_NOTES_STATE_IS_LOADING} from '/enum/coach.enum';
import {
  BBBActivationEndDate,
  BBBActivationStartDate,
  BBBBannerEndDate,
} from '/enum/dates.enum';
import {getOrders} from '/redux/action/client.action';
import {
  getPerformance,
  getProfile,
  getRankProgression,
  getSameDayLastMonth,
  getTeams,
  setCoachNotesState,
  updateCoachNotes,
} from '/redux/action/coach.action';
import {hasOrdersById, ordersSortParams} from '/redux/selector/client.selector';
import {
  activationDateById,
  fullNameById,
  hasBBBContributorsById,
  hasNextRankById,
  hasPerformanceById,
  hasProfileById,
  hasRankProgressionByCoachAndRankIds,
  hasSameDayLastMonthById,
  hasTeamsById,
  nextRankIdById,
  notesById,
  notesUpdatedTsById,
  paidAsRankDescLastMonth,
} from '/redux/selector/coach.selector';
import {userId} from '/redux/selector/currentUser.selector';
import {
  variablesBonusDisplayDate,
  variablesFeatureFlag,
} from '/redux/selector/remoteConfig.selector';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {fromProps, withProps} from '/util/selector.util';

import CoachProfile from './CoachProfile';

const withCoachId = withProps({
  coachId: fromProps(get('recordId')),
});

const withNextRankId = withProps({
  rankId: withCoachId(nextRankIdById),
});

const withCoachAndNextRankId = flow([withCoachId, withNextRankId]);

const withClientId = withProps({
  clientId: fromProps(get('recordId')),
});

export default connect(
  createStructuredSelector({
    fullName: withCoachId(fullNameById),
    nextRankId: withCoachId(nextRankIdById),
    hasProfile: withCoachId(hasProfileById),
    hasPerformance: withCoachId(hasPerformanceById),
    hasNextRank: withCoachId(hasNextRankById),
    hasRankProgression: withCoachAndNextRankId(
      hasRankProgressionByCoachAndRankIds,
    ),
    hasTeams: withCoachId(hasTeamsById),
    hasOrders: withClientId(hasOrdersById),
    ordersSorts: withClientId(ordersSortParams),
    hasSameDayLastMonth: withCoachId(hasSameDayLastMonthById),
    notes: withCoachId(notesById),
    notesUpdatedTs: withCoachId(notesUpdatedTsById),
    hasBusinessBuilderBonusContributors: withCoachId(hasBBBContributorsById),
    featureFlag: variablesFeatureFlag,
    coachActivationDate: withCoachId(activationDateById),
    bonusDisplayDate: variablesBonusDisplayDate,
    userId,
    lastPaidRank: withProps({coachId: userId})(paidAsRankDescLastMonth),
  }),
  dispatch => ({
    getProfile: ({coachId, shouldFetchProgression}: any) =>
      // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
      dispatch(getProfile({coachId, shouldFetchProgression})),
    // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
    getPerformance: ({coachId}: any) => dispatch(getPerformance({coachId})),
    getRankProgression: ({coachId, rankId}: any) =>
      // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
      dispatch(getRankProgression({coachId, rankId})),
    // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
    getTeams: ({coachId}: any) => dispatch(getTeams({coachId})),
    getOrders: ({coachId, orderParams, appendData}: any) =>
      // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
      dispatch(getOrders({clientId: coachId, orderParams, appendData})),
    getSameDayLastMonth: ({coachId}: any) =>
      // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
      dispatch(getSameDayLastMonth({coachId})),
    updateCoachNotes: ({coachId, body}: any) =>
      // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
      dispatch(updateCoachNotes({coachId, body})),
    setCoachNotesState: ({coachId, stateName, stateValue}: any) =>
      dispatch(
        // @ts-expect-error TS(2345): Argument of type '{ (dispatch: any, getState: any)... Remove this comment to see the full error message
        setCoachNotesState({
          coachId,
          stateName,
          stateValue,
        }),
      ),
  }),
  (stateProps, dispatchProps, ownProps) => {
    const {
      nextRankId,
      hasProfile,
      hasPerformance,
      hasNextRank,
      hasRankProgression,
      hasTeams,
      hasOrders,
      hasSameDayLastMonth,
      ordersSorts = {},
      featureFlag,
      coachActivationDate,
      bonusDisplayDate,
    } = stateProps;

    const {recordId: coachId} = ownProps;
    let hasData = false;

    const parsedBonusDisplayDate =
      typeof bonusDisplayDate === 'string'
        ? JSON.parse(bonusDisplayDate)
        : bonusDisplayDate || {};
    const {
      BBBActivationEndDate: remoteConfigBBBActivationEndDate,
      BBBActivationStartDate: remoteConfigBBBActivationStartDate,
      BBBBannerEndDate: remoteConfigBBBBannerEndDate,
    } = parsedBonusDisplayDate;

    const coachEligibleActivationEndDate =
      remoteConfigBBBActivationEndDate || BBBActivationEndDate;

    const coachEligibleActivationStartDate =
      remoteConfigBBBActivationStartDate || BBBActivationStartDate;

    const BBBEndDate = remoteConfigBBBBannerEndDate || BBBBannerEndDate;

    // We have to write these logic this way because we can not use the
    // value of `hasNextRank` until `hasProfile` is `true`.
    if (hasProfile && hasPerformance && hasTeams && hasOrders) {
      if (hasNextRank) {
        hasData = hasRankProgression;
      } else {
        hasData = true;
      }
    }

    return {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      hasData,
      fetchData: () => {
        let profilePromise = Promise.resolve();
        // We have to first check if `hasProfile` is `true` before we can ...
        if (hasProfile) {
          // ... use the value of `hasNextRank`.
          if (hasNextRank && !hasRankProgression) {
            // @ts-expect-error TS(2739): Type 'Action<any>' is missing the following proper... Remove this comment to see the full error message
            profilePromise = dispatchProps.getRankProgression({
              coachId,
              rankId: nextRankId,
            });
          }
        } else {
          // @ts-expect-error TS(2322): Type 'Action<any>' is not assignable to type 'Prom... Remove this comment to see the full error message
          profilePromise = dispatchProps.getProfile({
            coachId,
            shouldFetchProgression: true,
          });
        }

        let performancePromise = Promise.resolve();
        if (!hasPerformance) {
          // @ts-expect-error TS(2322): Type 'Action<any>' is not assignable to type 'Prom... Remove this comment to see the full error message
          performancePromise = dispatchProps.getPerformance({coachId});
        }
        let sameDayLastMonthPromise = Promise.resolve();
        if (!hasSameDayLastMonth) {
          // @ts-expect-error TS(2322): Type 'Action<any>' is not assignable to type 'Prom... Remove this comment to see the full error message
          sameDayLastMonthPromise = dispatchProps.getSameDayLastMonth({
            coachId,
          });
        }
        let teamsPromise = Promise.resolve();
        if (!hasTeams) {
          // @ts-expect-error TS(2322): Type 'Action<any>' is not assignable to type 'Prom... Remove this comment to see the full error message
          teamsPromise = dispatchProps.getTeams({coachId});
        }

        let ordersPromise = Promise.resolve();
        if (!hasOrders) {
          // @ts-expect-error TS(2322): Type 'Action<any>' is not assignable to type 'Prom... Remove this comment to see the full error message
          ordersPromise = dispatchProps.getOrders({
            coachId,
            orderParams: {
              ...ordersSorts,
              [PAGE_NUMBER_FIELD_NAME]: 1,
              [PAGE_SIZE_FIELD_NAME]: pageSize,
            },
            appendData: false,
          });
        }

        return Promise.all([
          profilePromise,
          performancePromise,
          teamsPromise,
          ordersPromise,
          sameDayLastMonthPromise,
        ]);
      },
      updateNotes: ({body}: any) => {
        return dispatchProps.updateCoachNotes({coachId, body});
      },
      setCoachNotesState: () => {
        return dispatchProps.setCoachNotesState({
          coachId,
          stateName: COACH_NOTES_STATE_IS_LOADING,
          stateValue: true,
        });
      },
      showBusinessBuilderBonusProfileCard:
        featureFlag?.showBusinessBuilderBonusBanner &&
        getDateUtilDefaultInstance().isWithinInterval(
          coachActivationDate,
          coachEligibleActivationStartDate,
          coachEligibleActivationEndDate,
        ) &&
        getDateUtilDefaultInstance().isDateSameOrBefore(BBBEndDate),
    };
  },
)(CoachProfile);
